import { ENDPOINT } from '~/constants';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import {
  HTTPError,
  vestigasApi,
  vestigasApiStaging,
} from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type TriggerSyncParams } from './types';
import {
  zodTriggerSyncEndpointSchema,
  zodTriggerSyncPayloadSchema,
} from './validations';

/**
 * Triggers a synchronization of data between environments (load data and create data in database).
 *
 * @param params - The parameters for the sync operation
 * @param params.syncSettings - The settings for the sync operation
 * @param params.syncSettings.apiKey - The API key to use for the sync
 * @param params.syncSettings.companyAccountId - The ID of the company account to sync
 * @param params.syncSettings.companyId - The ID of the company to sync
 * @param params.syncSettings.env - The environment to sync the data FROM (defaults to 'prod')
 * @param params.targetEnvironment - The environment to sync the data TO (defaults to 'staging')
 * @returns A promise that resolves with the API response containing the sync status
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Sync/operation/trigger_sync_sync_trigger_post
 */
export const triggerSync = async ({
  syncSettings,
  targetEnvironment = 'staging',
}: TriggerSyncParams) => {
  try {
    return await (
      targetEnvironment === 'staging' ? vestigasApiStaging : vestigasApi
    )
      .post(ENDPOINT.COMPANY.ACCOUNT.TRIGGER_SYNC(), {
        json: syncSettings,
        validate: {
          body: zodTriggerSyncPayloadSchema,
          pathSegments: zodTriggerSyncEndpointSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error triggering sync of company account data',
      error,
    });

    const errorMessage =
      error instanceof HTTPError && error.response.status === 422
        ? i18n.t(($) => $.companyAccount.triggerSync.error422, {
            ns: 'data',
          })
        : i18n.t(($) => $.companyAccount.triggerSync.error.generic, {
            ns: 'data',
          });

    ToastService.error(errorMessage);

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
